/**
 * Turning a component definition into a placeable block.
 *
 * `createBlock` (reducer.ts) is the low-level constructor — an id, a type and
 * whatever props the caller passes. The palette needs more than that, and the
 * difference is not cosmetic:
 *
 * - **Slots must exist up front.** `children` is one array per declared slot, and
 *   `slotsOf` reads its length. A container inserted without them has nowhere to
 *   drop into, on the canvas *or* in the layers tree, so it looks broken rather
 *   than empty.
 * - **Declared defaults must be materialized into props.** The compiler emits a
 *   prop even when it equals the component's default (see `emitArguments`), so a
 *   prop the document omits is simply absent from the `with { … }` list. A button
 *   inserted with no props therefore compiles without its background colour and
 *   trips the *missing required prop* preflight **error** immediately after being
 *   dropped.
 *
 * Manifest defaults are strings (`default=600`, `default="solid"`,
 * `default=false`, `default="{{ theme.colors.brand }}"`) because they come out of
 * a Twig comment. Coercing them per `ParamKind` here means the inspector gets
 * real numbers/booleans/`ThemeableColor`s to bind to, and `emitValue` compiles
 * them to byte-identical Twig either way.
 */
import { Block, EmailDocument, ThemeableColor, nextBlockId } from './document';
import {
  ManifestParam,
  ParamKind,
  isExpressionValue,
  unwrapExpression,
} from './manifest';
import { BlockDefinition } from './registry';
import { BlockLocation, findBlock, findLocation, slotsOf } from './tree';

/** `theme.colors.brand` — the only expression shape a colour default may take. */
const THEME_COLOR_PATTERN = /^theme\.colors\.([A-Za-z0-9_]+)$/;

/**
 * Starter values for **required** params a manifest gives no default for.
 *
 * Required-and-defaultless is the normal case for content (a button's text and
 * url), and leaving them empty would put a freshly inserted block into a
 * save-blocking error state. The values are passed in rather than hard-coded
 * because the text-ish ones are user-facing copy and must be translated at the
 * call site.
 */
export type ParamPlaceholders = Partial<Record<ParamKind, unknown>>;

/**
 * Parses a declared default into the value type the param's kind implies.
 * Returns undefined when the default cannot be read as that kind, so a typo in a
 * manifest degrades to "no default" rather than to a broken prop.
 */
export function coerceDefault(
  raw: string,
  kind: ParamKind,
): unknown | undefined {
  const value = raw.trim();
  if (value === '') return undefined;

  switch (kind) {
    case 'number': {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    case 'toggle': {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return undefined;
    }

    case 'color': {
      // A colour is themeable in the document model, so a `{{ theme.colors.x }}`
      // default becomes a token binding rather than an opaque expression string.
      // Both compile to the same Twig; only the token form is editable as
      // "Theme → brand" in the inspector.
      if (isExpressionValue(value)) {
        const token = unwrapExpression(value).match(THEME_COLOR_PATTERN);
        if (token) return { token: token[1] } as ThemeableColor;
        return value;
      }
      return { literal: value } as ThemeableColor;
    }

    default:
      // text / richtext / url / select / image / variable / spacing: verbatim.
      // An expression default stays a string — `emitValue` unwraps it.
      return value;
  }
}

/** The value a new block should carry for one declared param, if any. */
export function initialParamValue(
  param: ManifestParam,
  placeholders: ParamPlaceholders = {},
): unknown | undefined {
  if (param.default !== undefined) {
    const coerced = coerceDefault(param.default, param.kind);
    if (coerced !== undefined) return coerced;
  }
  return param.required ? placeholders[param.kind] : undefined;
}

export interface CreateBlockOptions {
  placeholders?: ParamPlaceholders;
  /** Overrides applied last, e.g. when duplicating a preset. */
  props?: Record<string, unknown>;
}

/**
 * A ready-to-insert block: declared defaults materialized, one empty array per
 * declared slot, and a fresh id (ids surface as `data-cvn-id`, so they must be
 * unique within the document for hit-testing to be unambiguous).
 */
export function createBlockFromDefinition(
  definition: BlockDefinition,
  options: CreateBlockOptions = {},
): Block {
  const props: Record<string, unknown> = {};

  for (const param of definition.params) {
    const value = initialParamValue(param, options.placeholders);
    if (value !== undefined) props[param.name] = value;
  }

  const block: Block = {
    id: nextBlockId(),
    type: definition.type,
    props: { ...props, ...options.props },
  };

  if (definition.slots.length > 0) {
    block.children = definition.slots.map(() => []);
  }

  return block;
}

/**
 * Where a click in the palette should put the new block.
 *
 * Relative to the selection, because that is where the user is looking:
 *
 * 1. **Nothing selected** → the end of the document.
 * 2. **A container selected** → inside it, at the end of `preferredSlot` (the slot
 *    the user last aimed at in the structure tree) or its first slot. Clicking
 *    *Text* right after inserting a *Section* has to fill the section, otherwise
 *    the section can only ever be filled by dragging.
 * 3. **Anything else selected** → directly after it, in the same slot, so
 *    inserting repeatedly builds downward instead of piling up at the end.
 *
 * `preferredSlot` is why a multi-column layout is usable without a mouse: without
 * it every click-insert landed in slot 0, so column 2 was reachable only by
 * dragging — and an empty column had no geometry to drag into either.
 */
export function insertionLocationFor(
  document: EmailDocument,
  selectedBlockId: string | null,
  preferredSlot?: number | null,
): BlockLocation {
  const atEnd: BlockLocation = {
    parentId: null,
    slot: 0,
    index: document.blocks.length,
  };
  if (!selectedBlockId) return atEnd;

  const selected = findBlock(document.blocks, selectedBlockId);
  if (!selected) return atEnd;

  const slots = slotsOf(selected);
  if (slots.length > 0) {
    // A stale preferred slot (the block changed, or its layout now renders fewer
    // columns) falls back to the first rather than pointing outside the block.
    const slot =
      preferredSlot != null && preferredSlot >= 0 && preferredSlot < slots.length
        ? preferredSlot
        : 0;
    return { parentId: selected.id, slot, index: slots[slot].length };
  }

  const location = findLocation(document.blocks, selectedBlockId);
  return location ? { ...location, index: location.index + 1 } : atEnd;
}
